import type { ParagraphTwoModelsCompare as ParagraphTwoModelsCompareData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import styles from "./ParagraphTwoModelsCompare.module.css";

/**
 * Layout 61 - `PARAGRAPH_TWO_MODELS_COMPARE_LAYOUT`: 2 karty porownawcze.
 *
 * Struktura:
 *   - Header: eyebrow (text_1) + tytul H2 (name) + lead (text)
 *   - Grid 2-kol z 2 kart:
 *       - Header karty: tytul (h3) + pill badge obok
 *       - Opis (paragraf)
 *       - Lista bulletow z checkiem (items[])
 *   - Druga karta zawsze highlighted (accent - zielony border + subtle mint bg)
 *   - Karta null gdy admin nie wypelnil zadnego pola (nie renderowana)
 */
export function ParagraphTwoModelsCompare({
  paragraph,
}: {
  paragraph: ParagraphTwoModelsCompareData;
  t: ParagraphTranslations;
}) {
  const { text_1: eyebrow, name: title, text: lead, cards } = paragraph;
  const hasEyebrow = eyebrow && eyebrow.trim() !== "";
  const hasLead = lead && lead.trim() !== "";
  const visibleCards = Array.isArray(cards) ? cards.filter((c) => c !== null) : [];
  const hasCards = visibleCards.length > 0;

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        {hasEyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
        {title ? <h2 className={styles.title}>{title}</h2> : null}
        {hasLead ? <p className={styles.lead}>{lead}</p> : null}
      </div>

      {hasCards ? (
        <div
          className={styles.grid}
          data-single={visibleCards.length === 1 ? "true" : "false"}
        >
          {visibleCards.map((card, idx) => {
            const hasHeader = card!.title || card!.badge;
            const hasDesc = Boolean(card!.desc);
            const hasItems = card!.items.length > 0;
            const cardHeader = hasHeader ? (
              <div className={styles.cardHeader}>
                {card!.title ? <h3 className={styles.cardTitle}>{card!.title}</h3> : null}
                {card!.badge ? <span className={styles.badge}>{card!.badge}</span> : null}
              </div>
            ) : null;
            const cardDesc = hasDesc ? <p className={styles.desc}>{card!.desc}</p> : null;
            const cardItems = hasItems ? (
              <ul className={styles.items}>
                {card!.items.map((item, i) => (
                  <li key={i} className={styles.item}>
                    <span className={styles.checkIcon} aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            ) : null;

            return (
              <div
                key={idx}
                className={styles.card}
                data-highlighted={card!.highlighted ? "true" : "false"}
              >
                {visibleCards.length === 1 ? (
                  <>
                    <div className={styles.cardMain}>
                      {cardHeader}
                      {cardDesc}
                    </div>
                    {cardItems}
                  </>
                ) : (
                  <>
                    {cardHeader}
                    {cardDesc}
                    {cardItems}
                  </>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
